<template>
  <div>
    <div
      class="product-filters critical-component-clear container"
      :class="{
        'filter-options--modal': isModal,
      }"
    >
      <h2
        v-if="isModal"
        class="filter-options__heading text-heading-2xl"
        v-text="$string('collection.filters.filter_by')"
      />

      <p
        v-if="isModal"
        class="text-body-xs product-filters__item-count"
        v-text="$string(
          'collection.sort_items',
          {
            replace: { count: productsCount }
          })"
      />

      <ul
        v-click-outside="handleClickOutside"
        class="product-filters__filters"
      >
        <li
          v-for="(filterOption, index) of cleanProductFilters"
          :key="filterOption.label"
          class="product-filters__filter"
          :class="{ 'is-open': openFilters.includes(filterOption.label) }"
          :style="`animation-delay: ${index * 0.15}s;`"
        >
          <button
            :ref="`${filterOption.label}-button`"
            class="button button--text product-filters__title"
            type="button"
            :aria-label="filterOption.label"
            @click="handleFilterOpenToggle(filterOption.label)"
          >
            <span
              class="text-body-s-bold text-body-s-desktop"
              v-text="filterOption.label"
            />

            <span class="product-filters__arrow">
              <icon-minus v-if="openFilters.includes(filterOption.label)" />
              <icon-plus v-else />
            </span>
          </button>

          <ul
            :ref="`${filterOption.label}-options`"
            class="product-filters__options"
            :class="{ 'product-filters__split-filters': filterOption.values.length > 6 }"
            :hidden="!openFilters.includes(filterOption.label)"
            :aria-label="filterOption.label"
            role="listbox"
          >
            <li
              v-for="(filter) of filterOption.values"
              :key="filter.values"
              class="product-filters__option"
            >
              <button
                class="product-filters__option-btn button button--text"
                role="option"
                type="button"
                :aria-label="filter.label"
                @click="handleFilterBy(filterOption, filter)"
              >
                <span
                  v-if="filter.active"
                  class="product-filters__icon"
                  :class="{ 'product-filters__icon--selected': filter.active }"
                >
                  <icon-tick />
                </span>

                <span
                  v-if="swatches.hasOwnProperty(filter.value)"
                  class="product-filters__color-hex"
                  :style="`background-color: ${swatches[filter.value]}`"
                />

                <span
                  v-if="filter.value"
                  :class="{ 'text-body-xs-bold': filter.active }"
                  class="product-filters__option-title text-body-xs"
                >{{ filter.value }}
                </span>
              </button>
            </li>
          </ul>
        </li>
      </ul>

      <div
        v-if="allProductsCount > 0"
        class="product-filters__info text-body-s"
      >
        <div v-if="!isModal">
          <p
            v-if="filterBy"
            class="text-body-s product-filters__item-count"
            v-text="$string(
              'collection.filter_items',
              {
                replace: { count: productsCount, count_total: allProductsCount }
              })"
          />

          <p
            v-else
            class="text-body-s product-filters__item-count"
            v-text="$string(
              'collection.sort_items',
              {
                replace: { count: allProductsCount }
              })"
          />
        </div>

        <p
          id="sort-label"
          class="text-body-s"
          v-html="$string('collection.sort_by')"
        />

        <div class="product-filters__sort">
          <select
            ref="sortSelect"
            class="product-filters__sort-select text-body-s"
            aria-describedby="a11y-refresh-page-message"
            aria-labelledby="sort-label"
            @change="handleSortChange"
          >
            <option
              v-for="option of sorting"
              :key="option.value"
              :value="option.value"
              :selected="currentSort === option.value"
              v-text="option.name"
            />
          </select>
        </div>
      </div>

      <div
        v-if="isActive"
        class="product-filters__footer"
      >
        <span
          class="text-body-s-link product-filters__footer-link"
          @click="handleClearFilters"
          v-html="$string('collection.filters.clear_all')"
        />

        <btn
          :label="$string(
            'collection.show_of',
            {
              replace: { count: productsCount, count_total: allProductsCount }
            })"
          modifiers="boxed"
          class="product-filters__footer-button"
          :show-icon="false"
          @click="closeFiltersOverlay"
        />
      </div>
    </div>

    <div
      v-if="transformedActiveFilters.length"
      class="product-filters__active container"
    >
      <button
        v-for="(filter) of transformedActiveFilters"
        :key="filter"
        class="button button--text product-filters__active-button"
        type="button"
        :aria-label="getActiveFilterLabel(filter)"
        @click="clearFilter(filter)"
      >
        <p
          class="text-body-xs"
          v-text="getActiveFilterLabel(filter)"
        />
        <span
          class="product-filters__active-button-icon"
        >
          <icon-close />
        </span>
      </button>

      <span
        class="text-body-xs-link product-filters__clear-all"
        @click="clearAllFilters"
        v-html="$string('collection.filters.clear_all')"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product filters (product-filters)
 * -----------------------------------------------------------------------------
 * Product Filters.
 *  @param {Boolean} isModal - Is used in modal.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import { nextTick } from 'vue'
import Btn from '~global/btn/btn.vue'

import IconMinus from '~icons/misc/minus.svg'
import IconPlus from '~icons/misc/plus.svg'
import IconTick from '~icons/misc/tick.svg'
import IconClose from '~icons/misc/close.svg'

export default {
  name: 'ProductFilters',

  components: {
    IconMinus,
    IconPlus,
    IconTick,
    IconClose,
    Btn,
  },

  props: {
    isModal: {
      type: Boolean,
      default: false,
    },
    currentSort: {
      type: String,
      default: 'default',
      required: true,
    },
    collectionHandle: {
      type: String,
      default: 'default',
      required: false,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    productFilters: {
      type: Array,
      default: () => [],
    },
    activeFilters: {
      type: Array,
      default: () => [],
    },
    sorting: {
      type: Array,
      default: () => [],
    },
    allProductsCount: {
      type: Number,
      default: 140,
    },
    productsCount: {
      type: Number,
      default: 140,
    },
    swatches: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: {
    'handle-sort-by': (payload) => typeof payload === 'string',
    'active-filters': (payload) => typeof payload === 'object',
    'clear-filters': () => true,
    'fetch-products': () => true,
  },

  data() {
    return {
      openFilters: [],
      query: [],
      filterBy: false,
      cleanProductFilters: [],
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getFirstElement: 'accessibility/getFirstElement',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      previousElement: (state) => state.accessibility.previousElement,
    }),

    /**
     * Returns the transformed active filters list.
     * @returns {Array}
     */
    transformedActiveFilters() {
      return this.activeFilters.filter((filter) => {
        return !filter.includes('filter.v.availability')
      })
    },

    /**
     * Returns if the filters contain the availability filter.
     * @returns {Boolean}
     */
    hasAvailabilityFilter() {
      return this.productFilters.find((item) => {
        return item.param_name === 'filter.v.availability'
      })
    },
  },

  watch: {

    /**
     * Watch isMobile to close overlay.
     */
    isMobile() {
      this.closeOverlay()
    },

    /**
     * Cleans product filters so they show correctly
     */
    productFilters() {
      this.cleanedProductFilters(this.productFilters)
    },

    /**
     * Updates query when active filter changes
     */
    activeFilters() {
      this.query = this.activeFilters
    },

    /**
     * Watch openFilters to update focus tate.
     */
    openFilters() {
      if (this.isModal) {
        return
      }

      this.updateFocusState()
    },

  },

  mounted() {

    this.setEventBusListeners()
    this.checkUrlParameters()
    this.cleanedProductFilters(this.productFilters)

    /**
     * set's the width of the select drop down
     */
    nextTick().then(() => {
      this.setSelectWidth()
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      closeOverlay: 'overlays/close',
      setFocusTrap: 'accessibility/setFocusTrap',
      setPreviousElement: 'accessibility/setPreviousElement',
    }),

    /**
     * Returns label for active filter
     */
    getActiveFilterLabel(filter) {
      const split = filter?.split('=')[1]
      if (split?.includes('colour:')) {
        return split?.split(':')[1]
      }

      return split?.replace('-', ' ')
    },

    /**
     * Checks to see if there are existing queries in the URL
     */
    checkUrlParameters() {
      const urlParameters = window.location.search

      if (urlParameters) {
        const querySplit = urlParameters.toString().split('?')[1].split('&').filter(query => !query.includes('q='))
        const validParams = querySplit.find(param => param.includes('filter'))
        const sortByExists = querySplit.filter(query => query.includes('sort_by='))

        if (validParams) {
          this.query = querySplit.map(filter => filter.replaceAll('%20', ' ')).filter(query => !query.includes('sort_by='))
          this.$emit('active-filters', this.query)
          if (sortByExists.length) {
            this.$emit('handle-sort-by', sortByExists[0].split('=')[1])
          }
          this.$emit('fetch-products', false, this.query)
          this.filterBy = true
          return
        }

        if (sortByExists.length) {
          this.$emit('handle-sort-by', sortByExists[0].split('=')[1])
        }

      }
    },

    /**
     * Handle clear specific filters
     */
    clearFilter(filter) {
      this.query.forEach(queryOption => {
        if (queryOption === filter) {
          this.query = this.query.filter(options => options !== queryOption)
        }
      })

      if (
        this.query.length === 1 &&
        this.query.includes('filter.v.availability=1')
      ) {
        this.query = []
      }

      this.$emit('fetch-products', false, this.query)

      this.productFilters.forEach(option => {
        option.values.find(value => {
          if (value.label === filter.split('=')[1]) {
            value.active = !value.active
            return true
          }
          return false
        })
      })

      if (this.query?.length === 0) {
        this.filterBy = false
      }

      this.$emit('active-filters', this.query)
    },

    /**
     * Handle clear all filters
     */
    clearAllFilters() {

      this.query = []
      this.filterBy = false
      this.sortBy = this.defaultSorting
      this.$emit('clear-filters')
      this.$emit('fetch-products', false, this.query)
      this.$emit('active-filters', this.query)

      cnvs.EventBus.emit('updateSelectWidth')

      this.productFilters.forEach(filter => {
        filter.values.map(option => {
          if (option.active) {
            option.active = !option.active
          }
          return option
        })
      })
    },

    /**
     * Set EventBus listeners.
     * - Listens for escape to be called.
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('cnvs:esc', () => {
        if (!this.openFilters.length) {
          return
        }

        this.openFilters = []
      })

      cnvs.EventBus.listen('updateSelectWidth', () => {
        this.setSelectWidth()
      })
    },

    /**
     * Cleans the filter tags to only include colour tags
     */
    cleanedProductFilters(filters) {

      filters.map(filterLabel => {
        if (filterLabel.label === 'More filters') {
          filterLabel.label = 'Color'
          const colorValues = []
          filterLabel.values.forEach((item) => {
            if (item.value.split(':')[0].toString() === 'colour') {
              item.value = item.value.split(':')[1]
              colorValues.push(item)
            }
          })
          filterLabel.values = colorValues
        }
        return filterLabel
      })

      this.cleanProductFilters = filters.filter((item) => {
        return item.param_name !== 'filter.v.availability'
      })
    },

    /**
     * Handle Filter by.
     * - Closes filter overlay on desktop after filtering.
     */
    handleFilterBy(filterOption, filter) {
      const filterLabel = this.productFilters.find(
        option => option.label === filterOption.label,
      )

      filterLabel.values.forEach(option => {
        if (option.label === filter.label) {
          filter.active = !filter.active
        }
      })

      this.filterBy = true
      let filterExists = false

      this.query.forEach(queryOption => {
        if (queryOption === `${filterOption.param_name}=${filter.label}`) {
          this.query = this.query.filter(options => options !== queryOption)
          filterExists = true
        }
      })

      if (!filterExists) {
        this.query.push(`${filterOption.param_name}=${filter.label}`)
      }

      if (
        this.hasAvailabilityFilter &&
        !this.query.includes('filter.v.availability=1')
      ) {
        this.query.push('filter.v.availability=1')
      }

      this.$emit('active-filters', this.query)
      this.$emit('fetch-products', false, this.query)
    },

    /**
     * Closes Overlay
     */
    closeFiltersOverlay() {
      this.closeOverlay()
    },

    /**
     * Set sort type & Emit new sort by value
     * @param {Object} event - Passed option.
     */
    handleSortChange(event) {
      this.setSelectWidth()
      this.$emit('handle-sort-by', event.target.value)

      const urlSortBy = this.query ? `?sort_by=${event.target.value}` : `?sort_by=${event.target.value}&`

      if (this.collectionHandle) {
        history.pushState({
          html: '',
        }, '', `${this.collectionHandle}${urlSortBy}`)
      }

      this.$emit('fetch-products', false, this.query)
    },

    /**
     * Handle clear filters.
     */
    handleClearFilters() {
      this.closeFiltersOverlay()
      this.$emit('clear-filters')
    },

    /**
     * Handle click outside.
     */
    handleClickOutside() {
      if (!this.openFilters.length || this.isModal) {
        return
      }

      this.openFilters = []
    },

    /**
     * Handle filter toggle.
     * - On mobile multiple filters can be open.
     * @param {String|Boolean} filterLabel - Current option.
     */
    handleFilterOpenToggle(filterLabel) {
      if (!this.isMobile) {
        this.openFilters = this.openFilters.includes(filterLabel)
          ? []
          : [filterLabel]
        return
      }

      /**
       * If filter is already open then remove from array.
       */
      const index = this.openFilters.findIndex((filter) => filter === filterLabel)

      if (index > -1) {
        this.openFilters.splice(index, 1)
        return
      }

      this.openFilters.push(filterLabel)
    },

    /**
     * Update focus trap using Vuex if filter is open.
     */
    updateFocusState() {
      if (this.openFilters.length) {
        this.setPreviousElement(document.activeElement)
        this.setFocusTrap({ container: this.$refs[`${this.openFilters[0]}-options`] })
        this.getFirstElement.focus()
        return
      }

      this.clearFocusState()
    },

    /**
     * Clear focus and previous element.
     */
    clearFocusState() {
      this.clearFocusTrap()
      this.previousElement.focus()
      this.setPreviousElement()
    },

    /**
     * Set width of sort element based on select option
     */
    setSelectWidth() {
      nextTick().then(() => {
        if (!this.$refs.sortSelect) {
          return
        }

        const widthElement = document.createElement('span')
        widthElement.innerHTML = this.$refs.sortSelect.options[this.$refs.sortSelect.selectedIndex].text || ''
        document.body.appendChild(widthElement)

        const width = widthElement.offsetWidth > 88
          ? widthElement.offsetWidth + 5
          : widthElement.offsetWidth + 15
        document.body.removeChild(widthElement)

        if (width === 0) {
          return
        }

        this.$refs.sortSelect.style.width = `${width}px`
      })
    },

  },
}
</script>

<style lang="scss">
@import './product-filters';
</style>
